/* eslint-disable class-methods-use-this */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Spinner, Pagination } from '@breezeway/brzw-components';

import emptyFilteredImage from './assets/no-results-empty.svg';
import errorImage from './assets/server-error-empty.svg';
import FlexAutoTableRow from './row';

import FlexTableHeader from './header';
import EmptyContentMessage from '../empty_content_message';
import SelectBox from './select_box';
import { PAGINATION_ARROWS } from './constants';

import './flex_table.scss';

const CLASS_NAME = 'flex-table';

const renderEmptyMessage = (title, subTitle) => (
  <EmptyContentMessage
    className={`${CLASS_NAME}__empty-message`}
    subTitle={subTitle}
    title={title}
  />
);

const renderEmptyFilteredMessage = () => (
  <EmptyContentMessage
    className={`${CLASS_NAME}__empty-message`}
    imageSrc={emptyFilteredImage}
    subTitle="Try adjusting your filters or search term."
    title="We couldn't find anything…"
    withoutIcon
  />
);

const renderErrorMessage = () => (
  <EmptyContentMessage
    className={`${CLASS_NAME}__empty-message`}
    imageSrc={errorImage}
    subTitle="Looks like a server error, try refreshing the page."
    title="Something went wrong…"
    withoutIcon
  />
);

const renderLoading = () => (
  <div className={`${CLASS_NAME}__loading-wrapper`}>
    <Spinner size="large" />
  </div>
);

const SELECTABLE_COLUMN_WIDTH = 40;
const getTableWidth = (columns, selectable) => {
  const tableWidths = _.map(columns, column => _.get(column, 'width', 0));
  return (
    _.sum(_.filter(tableWidths, tableWidth => _.isNumber(tableWidth))) +
    (selectable ? SELECTABLE_COLUMN_WIDTH : 0)
  );
};

const equalSelectedIds = (selectedIds, ids) => _.isEqual(selectedIds.sort(), ids.sort());

export class FlexTable extends Component {
  constructor(props) {
    super(props);

    this.state = { loading: true, error: false, pageSize: props.pageSize, currentPage: 1 };

    this.onChangePage = this.onChangePage.bind(this);
    this.onClearAllSelected = this.onClearAllSelected.bind(this);
    this.onClickPaginationArrow = this.onClickPaginationArrow.bind(this);
    this.onErrorLoad = this.onErrorLoad.bind(this);
    this.onSelectAllMatching = this.onSelectAllMatching.bind(this);
    this.onSelectPage = this.onSelectPage.bind(this);
    this.onSuccessLoad = this.onSuccessLoad.bind(this);
    this.rowRenderer = this.rowRenderer.bind(this);
  }

  componentDidMount() {
    const { loadData } = this.props;

    return loadData(this.onSuccessLoad, this.onErrorLoad);
  }

  componentDidUpdate(prevProps, prevState) {
    const { data, withPagination } = this.props;
    const { data: prevData } = prevProps;
    const { currentPage } = prevState;

    if (
      !_.isEqual(_.size(data), _.size(prevData)) &&
      withPagination &&
      !_.isEqual(currentPage, 1)
    ) {
      this.onChangePage(1);
    }
  }

  onSuccessLoad() {
    this.setState({
      loading: false,
    });
  }

  onErrorLoad() {
    this.setState({
      error: true,
      loading: false,
    });
  }

  onChangePage(currentPage) {
    this.setState({
      currentPage,
    });
  }

  onClickPaginationArrow(arrowSide = PAGINATION_ARROWS.RIGHT) {
    const { currentPage } = this.state;
    if (_.isEqual(arrowSide, PAGINATION_ARROWS.RIGHT)) {
      this.onChangePage(currentPage + 1);
    } else {
      this.onChangePage(currentPage - 1);
    }
  }

  onChangeSelected(selectableId, value) {
    const { onChangeSelected, selectedIds } = this.props;
    const ids = value ? [...selectedIds, selectableId] : _.without(selectedIds, selectableId);
    onChangeSelected(ids);
  }

  onSelectPage(displayedIds) {
    return () => {
      const { onChangeSelected } = this.props;
      onChangeSelected(displayedIds);
    };
  }

  onClearAllSelected() {
    const { onChangeSelected } = this.props;
    onChangeSelected([]);
  }

  onSelectAllMatching() {
    const { data, selectableAccessor, onChangeSelected } = this.props;
    const allMatchingIds = _.map(data, selectableAccessor);
    onChangeSelected(allMatchingIds);
  }

  rowRenderer(data, index) {
    const { columns, selectable, selectableAccessor, selectedIds } = this.props;
    const selectableId = _.get(data, selectableAccessor);
    const isSelected = _.findIndex(selectedIds, id => _.isEqual(id, selectableId)) > -1;

    return (
      <FlexAutoTableRow
        columns={columns}
        data={data}
        index={index}
        isSelected={isSelected}
        key={`item-key-${index}`}
        onChangeSelected={value => this.onChangeSelected(selectableId, value)}
        selectable={selectable}
      />
    );
  }

  renderPagination() {
    const { pageSize, currentPage } = this.state;
    const { data } = this.props;
    const pages = Math.ceil(_.size(data) / pageSize);

    return (
      <Pagination
        classname={`${CLASS_NAME}__pagination`}
        currentPage={currentPage}
        onArrowClick={this.onClickPaginationArrow}
        onPageClick={this.onChangePage}
        totalPagesCount={pages}
      />
    );
  }

  renderTableContent(data, width) {
    const { loading, error } = this.state;
    const { emptyMessage, filters, order, orderBy, searchValue } = this.props;

    const flexTableFilterKey = `filters-${JSON.stringify(filters)}-order-${order}-${orderBy}`;

    if (loading) {
      return renderLoading();
    }

    if (error) {
      return renderErrorMessage();
    }

    if (_.isEmpty(data)) {
      if (_.every(_.values(filters), filter => _.isEmpty(filter)) && _.isEmpty(searchValue)) {
        return renderEmptyMessage(
          _.get(emptyMessage, 'subTitle', ''),
          _.get(emptyMessage, 'title', ''),
        );
      }
      return renderEmptyFilteredMessage();
    }

    return (
      <div className={`${CLASS_NAME}__list`} key={flexTableFilterKey} style={{ minWidth: width }}>
        {_.map(data, this.rowRenderer)}
      </div>
    );
  }

  render() {
    const {
      changeOrder,
      clearFilters,
      columns,
      data,
      filters,
      name,
      onChangeSelected,
      order,
      orderBy,
      pageSize,
      selectable,
      selectableAccessor,
      selectedIds,
      setFilter,
      unsetFilter,
      withPagination,
    } = this.props;
    const { currentPage } = this.state;

    const dataCount = _.size(data);
    const shouldShowPagination = withPagination && dataCount > pageSize;
    const displayedData = shouldShowPagination
      ? _.slice(data, (currentPage - 1) * pageSize, currentPage * pageSize)
      : [...data];

    const displayedDataCount = _.size(displayedData);
    const displayedIds = _.map(displayedData, selectableAccessor);
    const selectedCount = _.size(selectedIds);
    const width = getTableWidth(columns, selectable);
    const shouldEqualSelected =
      !_.isEmpty(displayedIds) && _.isEqual(displayedDataCount, selectedCount);
    const allPageSelected = shouldEqualSelected
      ? equalSelectedIds(selectedIds, displayedIds)
      : false;

    const allMatchingSelected = !_.isEmpty(selectedIds) && _.isEqual(selectedCount, dataCount);
    const indeterminateSelected =
      !_.isEmpty(selectedIds) && !allPageSelected && !allMatchingSelected;
    const shouldShowSelectBox = (allPageSelected || allMatchingSelected) && shouldShowPagination;

    return (
      <div className={CLASS_NAME}>
        <div className={`${CLASS_NAME}__content`}>
          {shouldShowSelectBox && (
            <SelectBox
              allMatchingSelected={allMatchingSelected}
              dataCount={dataCount}
              objectName={name}
              onClearAll={this.onClearAllSelected}
              onSelectAllMatching={this.onSelectAllMatching}
              selectedCount={selectedCount}
            />
          )}
          <FlexTableHeader
            allSelected={allPageSelected || allMatchingSelected}
            changeOrder={changeOrder}
            clearFilters={clearFilters}
            columns={columns}
            filters={filters}
            indeterminateSelected={indeterminateSelected}
            onChangeSelected={onChangeSelected}
            onSelectAll={this.onSelectPage(displayedIds)}
            order={order}
            orderBy={orderBy}
            selectable={selectable}
            setFilter={setFilter}
            unsetFilter={unsetFilter}
            width={width}
          />
          {this.renderTableContent(displayedData, width)}
        </div>
        {shouldShowPagination && this.renderPagination()}
      </div>
    );
  }
}

FlexTable.propTypes = {
  changeOrder: PropTypes.func,
  clearFilters: PropTypes.func,
  columns: PropTypes.shape({}),
  data: PropTypes.array,
  emptyMessage: PropTypes.shape({
    subtitle: PropTypes.string,
    title: PropTypes.string,
  }),
  filters: PropTypes.shape({}),
  loadData: PropTypes.func.isRequired,
  name: PropTypes.string,
  onChangeSelected: PropTypes.func,
  order: PropTypes.string,
  orderBy: PropTypes.string,
  pageSize: PropTypes.number,
  searchValue: PropTypes.string,
  selectable: PropTypes.bool,
  selectableAccessor: PropTypes.string,
  selectedIds: PropTypes.arrayOf(PropTypes.number),
  setFilter: PropTypes.func,
  unsetFilter: PropTypes.func,
  withPagination: PropTypes.bool,
};

FlexTable.defaultProps = {
  data: [],
  name: '',
  pageSize: 25,
  searchValue: '',
  selectable: false,
  selectableAccessor: '',
  selectedIds: [],
  withPagination: false,
};

export default FlexTable;
